
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { messagingService } from '../../../services/Messaging/messagingService';
import { Notification } from '../../../types/messaging';

const NotificationListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const NotificationItem = styled.div<{ $isRead: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  background-color: ${props => props.$isRead ? 'transparent' : `${theme.colors.primary}0D`}; // 5% opacity
  cursor: pointer;
  transition: background-color ${theme.transitions.normal};

  &:hover {
    background-color: ${theme.colors.background.secondary};
  }
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xs};
  font-size: 0.95rem;
`;

const NotificationContent = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin-bottom: ${theme.spacing.xs};
  line-height: 1.4;
`;

const NotificationTime = styled.div`
  color: ${theme.colors.text.light}; // Muted text
  font-size: 0.75rem;
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.light};
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  padding: ${theme.spacing.xs};

  &:hover {
    color: ${theme.colors.text.secondary};
  }
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  color: ${theme.colors.text.secondary};
`;

const getIcon = (type: Notification['type']) => {
    switch (type) {
        case 'like': return '❤️';
        case 'match': return '🔥';
        case 'system': return '🔔';
        case 'bet': return '🎲';
        case 'message': return '💬';
        // Chat room activity: a comment, a reply, or the daily room digest.
        case 'community': return '🗣️';
        case 'comment': return '🗣️';
        default: return '📢';
    }
};

const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
};

export const NotificationList: React.FC = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const loadNotifications = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await messagingService.getNotifications();
            setNotifications(data);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    const handleMarkRead = async (notification: Notification) => {
        if (notification.isRead) return;
        setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n));
        try {
            await messagingService.markNotificationRead(notification.id);
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const handleDismiss = async (e: React.MouseEvent, notificationId: string) => {
        e.stopPropagation();
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        try {
            await messagingService.deleteNotification(notificationId);
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    if (isLoading) {
        return (
            <NotificationListContainer>
                <EmptyState>Loading notifications...</EmptyState>
            </NotificationListContainer>
        );
    }

    return (
        <NotificationListContainer>
            {notifications.length === 0 ? (
                <EmptyState>No notifications</EmptyState>
            ) : (
                notifications.map(notification => (
                    <NotificationItem
                        key={notification.id}
                        $isRead={notification.isRead}
                        onClick={() => handleMarkRead(notification)}
                    >
                        <div style={{ fontSize: '1.25rem' }}>{getIcon(notification.type)}</div>
                        <div style={{ flex: 1 }}>
                            <NotificationTitle>{notification.title}</NotificationTitle>
                            <NotificationContent>{notification.content}</NotificationContent>
                            <NotificationTime>{formatTime(notification.createdAt)}</NotificationTime>
                        </div>
                        <DismissButton onClick={(e) => handleDismiss(e, notification.id)} title="Dismiss">
                            ×
                        </DismissButton>
                    </NotificationItem>
                ))
            )}
        </NotificationListContainer>
    );
};
